'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/nextjs';
import { AlertCircle, Home, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
}

/**
 * 🛡️ Error Boundary with Sentry Integration
 *
 * Catches React errors and reports them to Sentry
 * Shows a user-friendly error UI with recovery options
 *
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // 📊 Report to Sentry with component stack
    const eventId = Sentry.captureException(error, {
      level: 'error',
      tags: {
        error_boundary: 'true',
        component_error: 'true',
      },
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });

    this.setState({
      errorInfo,
      eventId,
    });

    // Call optional error callback
    this.props.onError?.(error, errorInfo);

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught error:', error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/admin/dashboard';
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4">
          <div className="w-full max-w-md rounded-lg border border-red-200 bg-white p-8 shadow-lg">
            {/* Error Icon */}
            <div className="mb-6 flex justify-center">
              <div className="rounded-full bg-red-100 p-3">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
            </div>

            {/* Error Title */}
            <h2 className="mb-2 text-center text-2xl font-bold text-gray-900">
              Oops! Etwas ist schiefgelaufen
            </h2>

            {/* Error Description */}
            <p className="mb-6 text-center text-gray-600">
              Ein unerwarteter Fehler ist aufgetreten. Unser Team wurde automatisch benachrichtigt und
              arbeitet an einer Lösung.
            </p>

            {/* Error Details (Development Only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-6 rounded-md bg-gray-100 p-4">
                <p className="mb-2 text-sm font-semibold text-gray-700">Error Details:</p>
                <pre className="overflow-x-auto text-xs text-gray-600">
                  {this.state.error.message}
                </pre>
              </div>
            )}

            {/* Sentry Event ID */}
            {this.state.eventId && (
              <p className="mb-6 text-center text-xs text-gray-500">
                Error ID: {this.state.eventId}
              </p>
            )}

            {/* Action Buttons */}
            <div className="space-y-2">
              <Button onClick={this.handleReset} className="w-full" variant="default">
                <RefreshCw className="mr-2 h-4 w-4" />
                Erneut versuchen
              </Button>

              <Button onClick={this.handleReload} className="w-full" variant="outline">
                <RefreshCw className="mr-2 h-4 w-4" />
                Seite neu laden
              </Button>

              <Button onClick={this.handleGoHome} className="w-full" variant="ghost">
                <Home className="mr-2 h-4 w-4" />
                Zurück zum Dashboard
              </Button>
            </div>

            {/* Support Info */}
            <p className="mt-6 text-center text-xs text-gray-500">
              Wenn das Problem weiterhin besteht, kontaktieren Sie bitte unseren Support.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * 🎯 Lightweight Error Boundary (minimal UI)
 *
 * Use this for smaller components where you want minimal error UI
 */
export function MinimalErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallback={
        <div className="rounded-md border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">Fehler beim Laden dieser Komponente.</p>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
}
